import {UserSubscriptionPlan} from "../user-subscription-plan";
import {PlanFeature} from './plan-feature';
import {TypeUtils} from "../../shared/utils/type-utils";

export interface IPlanDescriptionInstance {

  title: string;
  titleShort: string;
  premiumPluginKey: string;
  pluginTitle: string;
  pluginTitleShort: string;
  serviceTitle: string;
  serviceTitleShort: string;
  type: string;
  domainsCount: number;
  periodType: string;
  periodNumber: number;
  price: number;
  planPrice: number;
  couponDiscount: number;
  proratedCredit: number;
  priceDiscount: number;
  priceDiscounted: number;
  priceWithProratedCredit: number;
  mainFeatures: PlanFeature[];
  additionalFeatures: PlanFeature[];

}

export class PlanDescription {

  public plan: UserSubscriptionPlan;
  public description: any;

  public descriptionMap: any;

  constructor(userSubscriptionPlan: UserSubscriptionPlan, description: any, descriptionMap: any) {
    this.plan = userSubscriptionPlan;
    this.description = description;
    this.descriptionMap = descriptionMap;
  }

  public getFeatures(pluginKey?: string): IPlanDescriptionInstance {
    pluginKey = pluginKey || this.description.premiumPluginKey;

    const pluginData: any = null; /*this.getPluginMapData(pluginKey);*/

    const mainFeatures: PlanFeature[] = this.description.features && this.description.features.main ? this.description.features.main.reduce((features: PlanFeature[], featureData: any): PlanFeature[] => {
      return features.concat(this.parseFeatureData(featureData, pluginKey));
    }, []) : [];

    const additionalFeatures: PlanFeature[] = this.description.features && this.description.features.additional ? this.description.features.additional.reduce((features: PlanFeature[], featureData: any): PlanFeature[] => {
      return features.concat(this.parseFeatureData(featureData, pluginKey));
    }, []) : [];

    return {
      title: this.descriptionMap.globalTitle,
      titleShort: this.descriptionMap.globalTitleShort,
      premiumPluginKey: this.description.premiumPluginKey,
      pluginTitle: pluginData ? pluginData.title : '',
      pluginTitleShort: pluginData ? pluginData.titleShort : '',
      serviceTitle: '',
      serviceTitleShort: '',
      type: this.plan.type,
      domainsCount: this.plan.domainCount,
      periodType: this.plan.periodTypeText,
      periodNumber: this.plan.periodNumber,
      price: this.plan.price,
      planPrice: this.plan.planPrice,
      couponDiscount: this.plan.couponDiscount,
      proratedCredit: this.plan.proratedCredit,
      priceDiscount: this.plan.priceDiscount,
      priceDiscounted: this.plan.priceDiscounted,
      priceWithProratedCredit: this.plan.priceWithProratedCredit,
      mainFeatures: mainFeatures,
      additionalFeatures: additionalFeatures
    };
  }

  public getComparisonFeatures(pluginKey?: string): IPlanDescriptionInstance {
    const mainFeatures: PlanFeature[] = this.description.features_comparison && this.description.features_comparison.main ? this.description.features_comparison.main.reduce((features: PlanFeature[], featureData: any): PlanFeature[] => {
      return features.concat(this.parseFeatureData(featureData, pluginKey));
    }, []) : [];

    const additionalFeatures: PlanFeature[] = this.description.features_comparison && this.description.features_comparison.additional ? this.description.features_comparison.additional.reduce((features: PlanFeature[], featureData: any): PlanFeature[] => {
      return features.concat(this.parseFeatureData(featureData, pluginKey));
    }, []) : [];

    return {
      title: this.descriptionMap.globalTitle,
      titleShort: this.descriptionMap.globalTitleShort,
      premiumPluginKey: this.description.premiumPluginKey,
      pluginTitle: '',
      pluginTitleShort: '',
      serviceTitle: '',
      serviceTitleShort: '',
      type: this.plan.type,
      domainsCount: this.plan.domainCount,
      periodType: this.plan.periodTypeText,
      periodNumber: this.plan.periodNumber,
      price: this.plan.price,
      couponDiscount: this.plan.couponDiscount,
      proratedCredit: this.plan.proratedCredit,
      planPrice: this.plan.planPrice,
      priceDiscount: this.plan.priceDiscount,
      priceDiscounted: this.plan.priceDiscounted,
      priceWithProratedCredit: this.plan.priceWithProratedCredit,
      mainFeatures: mainFeatures,
      additionalFeatures: additionalFeatures
    };
  }

  public getServiceFeatures(serviceKey: string, shortFeatures): IPlanDescriptionInstance {
    const parsedServiceKeys: string[] = [];
    let value;
    if (shortFeatures) {
      value = shortFeatures[serviceKey];
    }
    const mainService: any = this.getServiceMapData(serviceKey);

    const mainFeatures: PlanFeature[] = this.description.features_service && this.description.features_service.main ? this.description.features_service.main.reduce((features: PlanFeature[], featureData: any): PlanFeature[] => {
      return features.concat(this.parseFeatureData(featureData, '', serviceKey, parsedServiceKeys));
    }, []) : [];

    const additionalFeatures: PlanFeature[] = this.description.features_service && this.description.features_service.additional ? this.description.features_service.additional.reduce((features: PlanFeature[], featureData: any): PlanFeature[] => {
      return features.concat(this.parseFeatureData(featureData, '', serviceKey, parsedServiceKeys, shortFeatures));
    }, []) : [];

    return {
      title: this.descriptionMap.globalTitle,
      titleShort: this.descriptionMap.globalTitleShort,
      premiumPluginKey: this.description.premiumPluginKey,
      pluginTitle: '',
      pluginTitleShort: '',
      serviceTitle: mainService ? mainService.title.split('{{value}}').join(value ? value : mainService.defaultValue) : '',
      serviceTitleShort: mainService && mainService.titleShort ? mainService.titleShort.split('{{value}}').join(value ? value : mainService.defaultValue) : '',
      type: this.plan.type,
      domainsCount: this.plan.domainCount,
      periodType: this.plan.periodTypeText,
      periodNumber: this.plan.periodNumber,
      planPrice: this.plan.planPrice,
      price: this.plan.price,
      couponDiscount: this.plan.couponDiscount,
      proratedCredit: this.plan.proratedCredit,
      priceDiscount: this.plan.priceDiscount,
      priceDiscounted: this.plan.priceDiscounted,
      priceWithProratedCredit: this.plan.priceWithProratedCredit,
      mainFeatures: mainFeatures,
      additionalFeatures: additionalFeatures
    };
  }

  private parseFeatureData(planFeatureData: any, pluginKey?: string, serviceKey?: string, parsedServiceKeys: string[] = [], shortDesc?: any): PlanFeature[] {
    let features: PlanFeature[];
    switch (planFeatureData.type) {
      case 'common_feature':
        features = this.parseCommonFeature(planFeatureData);
        break;

      case 'plugin':
        features = this.parsePluginFeature(planFeatureData, pluginKey);
        break;

      case 'plugins':
        features = this.parsePluginsFeature(planFeatureData, pluginKey);
        break;

      case 'service':
        features = this.parseServiceFeature(planFeatureData, serviceKey, parsedServiceKeys, shortDesc);
        break;
    }

    if (planFeatureData.expandFeatures) {
      features = features.reduce((features: PlanFeature[], feature: PlanFeature): PlanFeature[] => {
        return feature.features ? features.concat(feature.features) : features;
      }, []);
    }

    return features;
  }

  private parseCommonFeature(commonFeatureData: any): PlanFeature[] {
    const commonFeatureMapData: any = this.getCommonFeatureMapData(commonFeatureData.key);
    if (!commonFeatureMapData) {
      return [];
    }
    const featureValue: any = TypeUtils.isDefined(commonFeatureData.value) ? commonFeatureData.value : commonFeatureMapData.defaultValue;
    const feature: PlanFeature = new PlanFeature();
    feature.title = commonFeatureMapData.title ? commonFeatureMapData.title.split('{{value}}').join(featureValue) : '';
    feature.titleShort = commonFeatureMapData.titleShort ? commonFeatureMapData.titleShort.split('{{value}}').join(featureValue) : '';
    feature.info = commonFeatureMapData.info;
    feature.infoShort = commonFeatureMapData.infoShort;
    feature.hasInfoIcon = commonFeatureData.hasInfoIcon;
    feature.link = commonFeatureMapData.link;
    feature.available = !!commonFeatureData.available;

    return [feature];
  }

  private getCommonFeatureMapData(key: string): any {
    return this.descriptionMap.commonFeatures[key];
  }

  private parsePluginFeature(pluginData: any, pluginKey?: string): PlanFeature[] {
    const pluginMapData: any = this.getPluginMapData(pluginKey || pluginData.key);
    if (!pluginMapData) {
      return [];
    }

    const featureValue: any = TypeUtils.isDefined(pluginData.value) ? pluginData.value : pluginMapData.defaultValue;
    const feature: PlanFeature = new PlanFeature();
    feature.title = pluginMapData.title ? pluginMapData.title.split('{{value}}').join(pluginData.value) : '';
    feature.titleShort = pluginMapData.titleShort ? pluginMapData.titleShort.split('{{value}}').join(pluginData.value) : '';
    feature.info = pluginMapData.info;
    feature.infoShort = pluginMapData.infoShort;
    feature.hasInfoIcon = pluginMapData.hasInfoIcon;
    feature.link = pluginMapData.link;
    feature.available = !!pluginData.available;
    let features: PlanFeature[];
    if (pluginData.features) {
      features = pluginData.features
        .map((pluginFeatureData: any): PlanFeature => {
          const pluginFeatureMapData: any = this.getPluginFeatureMapData(pluginMapData, pluginFeatureData.key);
          if (!pluginFeatureMapData) {
            return null;
          }

          const featureValue: any = TypeUtils.isDefined(pluginFeatureData.value) ? pluginFeatureData.value : pluginFeatureMapData.defaultValue;
          const feature: PlanFeature = new PlanFeature();
          feature.title = pluginFeatureMapData.title ? pluginFeatureMapData.title.split('{{value}}').join(featureValue) : '';
          feature.titleShort = pluginFeatureMapData.titleShort ? pluginFeatureMapData.titleShort.split('{{value}}').join(featureValue) : '';
          feature.info = pluginFeatureMapData.info;
          feature.infoShort = pluginFeatureMapData.infoShort;
          feature.hasInfoIcon = pluginFeatureMapData.hasInfoIcon;
          feature.link = pluginFeatureMapData.link;
          feature.available = !!pluginFeatureData.available;
          return feature;
        })
        .filter((feature: PlanFeature) => !!feature);
    }
    else if (pluginMapData.features) {
      features = pluginMapData.features
        .filter((pluginFeatureMapData: any) => !pluginFeatureMapData.hidden)
        .map((pluginFeatureMapData: any): PlanFeature => {
          const featureValue: any = pluginFeatureMapData.defaultValue;
          const feature: PlanFeature = new PlanFeature();
          feature.title = pluginFeatureMapData.title ? pluginFeatureMapData.title.split('{{value}}').join(featureValue) : '';
          feature.titleShort = pluginFeatureMapData.titleShort ? pluginFeatureMapData.titleShort.split('{{value}}').join(featureValue) : '';
          feature.info = pluginFeatureMapData.info;
          feature.infoShort = pluginFeatureMapData.infoShort;
          feature.hasInfoIcon = pluginFeatureMapData.hasInfoIcon;
          feature.link = pluginFeatureMapData.link;
          feature.available = !!pluginFeatureMapData.available;
          return feature;
        });
    }
    else {
      features = [];
    }
    feature.features = features;

    return [feature];
  }

  private getPluginMapData(key: string): any {
    return this.descriptionMap && this.descriptionMap.plugins && this.descriptionMap.plugins.list
      && this.descriptionMap.plugins.list.find((pluginData: any): any => pluginData.key === key);
  }

  private getPluginFeatureMapData(pluginMapData: any, pluginFeatureKey: string): any {
    return pluginMapData.features.find((pluginFeature: any): boolean => pluginFeature.key === pluginFeatureKey);
  }

  private parsePluginsFeature(pluginsData: any, pluginKey?: string): PlanFeature[] {
    const features: PlanFeature[] = [];
    const mainPluginKey: string = pluginKey || pluginsData.key;
    const mainPluginData: any = null; /*this.getPluginMapData(mainPluginKey);*/
    const mainPluginExtensions: any[] = mainPluginData && pluginsData.showExtensions ? mainPluginData.extensions : [];
    const allPluginDatas: any[] = this.descriptionMap.plugins.list.filter((pluginData: any): any => pluginData.key !== mainPluginKey);
    let allPluginsCount: number = this.descriptionMap.plugins.totalPluginsAndExtensions;
    let allPluginCount: number = this.descriptionMap.plugins.totalPlugins;
    let allExtensionCount: number = this.descriptionMap.plugins.totalExtensions;
    if (mainPluginData) {
      --allPluginsCount;
      --allPluginCount;
      --allExtensionCount;
    }
    if (mainPluginExtensions && mainPluginExtensions.length) {
      allPluginsCount -= mainPluginExtensions.length;
    }

    if (mainPluginData && !pluginsData.hideMainPlugin) {
      const mainPluginFeature: PlanFeature = new PlanFeature();
      mainPluginFeature.title = mainPluginData.title;
      mainPluginFeature.available = pluginsData.available;
      features.push(mainPluginFeature);
    }

    const allPluginTitles: string[] = [];
    const allPluginFeatures: PlanFeature[] = [];
    allPluginDatas.forEach((pluginData: any, index: number): void => {
      allPluginTitles.push(pluginData.title);

      if (index < 3) {
        const pluginFeature: PlanFeature = new PlanFeature();
        pluginFeature.title = index === 2 && allPluginsCount > 3 ? pluginData.title + ' & etc.' : pluginData.title;
        pluginFeature.available = !!pluginsData.allAvailable;
        allPluginFeatures.push(pluginFeature);
      }
    });
    const allPluginsFeature: PlanFeature = new PlanFeature();
    let allPluginsCountString;
    if (pluginsData.showExtensions) {
      allPluginsCountString = "+" + allPluginsCount;
    }
    else {
      allPluginsCountString = allPluginsCount + "+";
    }
    allPluginsFeature.title = this.descriptionMap.plugins.title.split('{{value}}').join(allPluginsCountString);
    allPluginsFeature.info = this.descriptionMap.plugins.info;
    allPluginsFeature.allPluginsCount = allPluginCount;
    allPluginsFeature.allExtensionsCount = allExtensionCount;
    allPluginsFeature.infoShort = this.descriptionMap.plugins.info;
    allPluginsFeature.hasInfoIcon = true;
    allPluginsFeature.link = this.descriptionMap.plugins.link;
    allPluginsFeature.features = allPluginFeatures;
    allPluginsFeature.available = !!pluginsData.allAvailable;
    features.push(allPluginsFeature);

    if (mainPluginData && mainPluginExtensions && mainPluginExtensions.length) {
      const extensionTitles: string[] = [];
      const allExtensionFeatures: PlanFeature[] = [];
      mainPluginExtensions.forEach((extensionData: any, index: number): void => {
        extensionTitles.push(extensionData.title);

        if (index < 3) {
          const extensionFeature: PlanFeature = new PlanFeature();
          extensionFeature.title = index === 2 && mainPluginExtensions.length > 3 ? extensionData.title + ' & etc.' : extensionData.title;
          extensionFeature.available = !!pluginsData.available;
          allExtensionFeatures.push(extensionFeature);
        }
      });
      const extensionsFeature: PlanFeature = new PlanFeature();
      extensionsFeature.title = `+${mainPluginExtensions.length} Premium ${mainPluginData.title} extensions`;
      extensionsFeature.info = 'Expand plugin\'s functionality with ' + extensionTitles.length + ' extensions.';
      extensionsFeature.infoShort = 'Expand plugin\'s functionality with ' + extensionTitles.length + ' extensions.';
      extensionsFeature.hasInfoIcon = true;
      extensionsFeature.features = allExtensionFeatures;
      extensionsFeature.available = !!pluginsData.available;
      features.push(extensionsFeature);
    }

    return features;
  }

  private parseServiceFeature(serviceData: any, serviceKey?: string, parsedServiceKeys: string[] = [], shortDesc?: any): PlanFeature[] {
    if (serviceData.key) {
      serviceKey = serviceData.key;
    }
    else if (!serviceKey || parsedServiceKeys.includes(serviceKey)) {
      serviceKey = this.descriptionMap.services.list
        .map((serviceData: any) => serviceData.key)
        .find((key: string): boolean => {
          return !parsedServiceKeys.includes(key);
        });
    }

    parsedServiceKeys.push(serviceKey);

    const serviceMapData: any = this.getServiceMapData(serviceKey);
    if (!serviceMapData) {
      return [];
    }
    const featureValue: any = TypeUtils.isDefined(serviceData.value) ? serviceData.value : shortDesc && shortDesc[serviceKey] ? shortDesc[serviceKey] : serviceMapData.defaultValue;
    const feature: PlanFeature = new PlanFeature();
    feature.title = serviceMapData.title ? serviceMapData.title.split('{{value}}').join(featureValue) : '';
    feature.titleShort = serviceMapData.titleShort ? serviceMapData.titleShort.split('{{value}}').join(featureValue) : '';
    feature.info = serviceMapData.info;
    feature.infoShort = serviceMapData.infoShort;
    feature.hasInfoIcon = serviceData.hasInfoIcon;
    feature.link = serviceMapData.link;
    feature.available = !!serviceData.available;
    feature.value = featureValue;
    let features: PlanFeature[];
    if (serviceData.features) {
      features = serviceData.features
        .map((serviceFeatureData: any): PlanFeature => {
          const serviceFeatureMapData: any = this.getServiceFeatureMapData(serviceMapData, serviceFeatureData.key);
          if (!serviceFeatureMapData) {
            return null;
          }

          const featureValue: any = TypeUtils.isDefined(serviceFeatureData.value) ? serviceFeatureData.value : serviceFeatureMapData.defaultValue;
          const feature: PlanFeature = new PlanFeature();
          feature.title = serviceFeatureMapData.title ? serviceFeatureMapData.title.split('{{value}}').join(featureValue) : '';
          feature.titleShort = serviceFeatureMapData.titleShort ? serviceFeatureMapData.titleShort.split('{{value}}').join(featureValue) : '';
          feature.info = serviceFeatureMapData.info;
          feature.infoShort = serviceFeatureMapData.infoShort;
          feature.hasInfoIcon = serviceFeatureMapData.hasInfoIcon;
          feature.link = serviceFeatureMapData.link;
          feature.available = !!serviceFeatureData.available;
          feature.hidden = !!serviceFeatureData.hidden;
          return feature;
        })
        .filter((feature: PlanFeature) => !!feature);
    }
    else if (serviceMapData.features) {
      features = serviceMapData.features
        .filter((serviceFeatureMapData: any) => !serviceFeatureMapData.hidden)
        .map((serviceFeatureMapData: any): PlanFeature => {
          const featureValue: any = serviceFeatureMapData.defaultValue;
          const feature: PlanFeature = new PlanFeature();
          feature.title = serviceFeatureMapData.title ? serviceFeatureMapData.title.split('{{value}}').join(featureValue) : '';
          feature.titleShort = serviceFeatureMapData.titleShort ? serviceFeatureMapData.titleShort.split('{{value}}').join(featureValue) : '';
          feature.info = serviceFeatureMapData.info;
          feature.infoShort = serviceFeatureMapData.infoShort;
          feature.hasInfoIcon = serviceFeatureMapData.hasInfoIcon;
          feature.link = serviceFeatureMapData.link;
          feature.available = !!serviceFeatureMapData.available;
          return feature;
        });
    }
    else {
      features = [];
    }
    feature.features = features;

    return [feature];
  }

  private getServiceMapData(serviceKey: string): any {
    return this.descriptionMap.services.list.find((service: any): boolean => service.key === serviceKey);
  }

  private getServiceFeatureMapData(serviceMapData: any, serviceFeatureKey: string): any {
    return serviceMapData.features.find((serviceFeature: any): boolean => serviceFeature.key === serviceFeatureKey);
  }

}
